import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, ShieldOff, RefreshCw, KeyRound, Terminal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Execution() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mcp/pilot/status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      toast.error("Could not load pilot status", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/mcp/pilot/generate`, {}, {
        withCredentials: true,
        responseType: "blob",
      });
      const keyId = res.headers["x-pilot-key-id"] || "unknown";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `neos_openclaw_pilot_${keyId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Pilot package generated", { description: `Key ${keyId} · read-only · downloaded.` });
      load();
    } catch (e) {
      toast.error("Generate failed", { description: e?.response?.data?.detail || e.message });
    } finally { setGenerating(false); }
  };

  const revokeAll = async () => {
    if (!window.confirm("Revoke ALL pilot keys? OpenClaw on the Founder's Mac will lose access within one HTTP round-trip. NEOS core stays online.")) return;
    setRevoking(true);
    try {
      const { data } = await axios.post(`${API}/mcp/pilot/revoke-all`, {}, { withCredentials: true });
      toast.success(`Revoked ${data.revoked_count} pilot key${data.revoked_count === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error("Revoke failed", { description: e?.response?.data?.detail || e.message });
    } finally { setRevoking(false); }
  };

  const revokeOne = async (keyId) => {
    if (!window.confirm(`Revoke pilot key ${keyId}?`)) return;
    try {
      await axios.delete(`${API}/mcp/keys/${keyId}`, { withCredentials: true });
      toast.success(`Revoked ${keyId}`);
      load();
    } catch (e) {
      toast.error("Revoke failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="execution-page">
      <header className="space-y-1">
        <h1 className="text-3xl font-serif text-forest">Execution · OpenClaw Pilot</h1>
        <p className="text-sm text-forest/70">
          Issue and revoke read-only MCP credentials for the Founder-controlled OpenClaw Mac pilot.
          <span className="font-medium"> No production writes. No recurring cost. Kill switch always available.</span>
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Download size={18} /> Generate pilot package</CardTitle>
            <CardDescription>Issues a fresh read-only key and downloads the ZIP with the token baked in.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button data-testid="pilot-generate-btn" onClick={generate} disabled={generating} className="w-full">
              {generating ? "Generating…" : "Generate & Download"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ShieldOff size={18} /> Revoke ALL pilot keys</CardTitle>
            <CardDescription>Instant kill switch. All active pilot keys → 401 within one round-trip.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button data-testid="pilot-revoke-all-btn" variant="destructive" onClick={revokeAll} disabled={revoking} className="w-full">
              {revoking ? "Revoking…" : "Revoke Pilot Keys"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><RefreshCw size={18} /> Refresh status</CardTitle>
            <CardDescription>Re-fetch the pilot-key ledger from NEOS.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button data-testid="pilot-refresh-btn" onClick={load} disabled={loading} variant="outline" className="w-full">
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound size={18} /> Pilot Key Ledger</CardTitle>
          <CardDescription>
            {status ? (
              <>Total {status.total} · Active <span className="text-green-700 font-medium">{status.active}</span> · Revoked <span className="text-red-700 font-medium">{status.revoked}</span></>
            ) : "Loading…"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pilot-keys-table">
              <thead className="text-left border-b border-forest/10">
                <tr>
                  <th className="py-2 pr-4">Key ID</th>
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4">Issued</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Scopes</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {(status?.keys ?? []).map(k => (
                  <tr key={k.key_id} className="border-b border-forest/5" data-testid={`pilot-key-row-${k.key_id}`}>
                    <td className="py-2 pr-4 font-mono text-xs">{k.key_id}</td>
                    <td className="py-2 pr-4">{k.name}</td>
                    <td className="py-2 pr-4 text-xs text-forest/60">{k.created_at?.slice(0, 19)}</td>
                    <td className="py-2 pr-4">
                      {k.revoked
                        ? <Badge variant="destructive" data-testid={`pilot-key-status-${k.key_id}`}>REVOKED</Badge>
                        : <Badge className="bg-green-700" data-testid={`pilot-key-status-${k.key_id}`}>ACTIVE</Badge>}
                    </td>
                    <td className="py-2 pr-4 text-xs text-forest/70">{(k.scopes || []).join(", ")}</td>
                    <td className="py-2 pr-4 text-right">
                      {!k.revoked && (
                        <Button size="sm" variant="ghost" onClick={() => revokeOne(k.key_id)} data-testid={`pilot-key-revoke-${k.key_id}`}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {status && status.keys?.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-forest/50">No pilot keys issued yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Terminal size={18} /> Founder Mac commands</CardTitle>
          <CardDescription>Run these on the Mac after extracting the downloaded ZIP.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-forest text-bone p-4 rounded overflow-x-auto" data-testid="pilot-mac-commands">
{`# 1. Extract the downloaded ZIP
unzip neos_openclaw_pilot_<KEY_ID>.zip
cd openclaw_pilot

# 2. Install OpenClaw + copy config (openclaw.json already contains your token)
bash install.sh

# 3. Onboard, probe, and run
openclaw onboard
openclaw mcp probe neos
openclaw run "Read NEOS opportunity counts"

# 4. Verify write is denied (expect HTTP 403)
openclaw run "Create a grant draft titled 'pilot denial test'"

# 5. Kill switch — from this admin page click "Revoke Pilot Keys",
#    or from the Mac: openclaw mcp remove neos`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
